import { useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { baseURL } from "services/API";

const einvoicingAdminBase = `${baseURL}/einvoicing/admin/landlords`;

const einvoicingErrorMessage = (error, fallback) => {
	const data = error?.response?.data;
	if (!data) return error?.message || fallback;
	if (typeof data === "string") return data;
	if (data.message) return data.message;
	if (data.error) {
		if (typeof data.error === "string") return data.error;
		if (Array.isArray(data.error)) return data.error[0];
	}
	return fallback;
};

/** Enable / disable landlord e-invoicing (URA EFRIS provider). */
export const useEinvoicing = () => {
	const token = useSelector((state) => state.token);
	axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

	const [isSubmitting, setSubmitting] = useState(false);

	const setEinvoicingEnabled = async (landlordId, einvoicing_enabled) => {
		if (!landlordId) return { success: false, message: "Missing landlord." };
		setSubmitting(true);
		try {
			const response = await axios.post(`${einvoicingAdminBase}/${landlordId}/enable`, {
				einvoicing_enabled,
			});
			return { success: true, data: response.data };
		} catch (error) {
			return {
				success: false,
				message: einvoicingErrorMessage(error, "Failed to update e-invoicing."),
			};
		} finally {
			setSubmitting(false);
		}
	};

	const issueInvoice = async (landlordId, payload) => {
		if (!landlordId) {
			const err = new Error("Missing landlord.");
			err.response = { data: { error: "Missing landlord." } };
			throw err;
		}
		const response = await axios.post(
			`${einvoicingAdminBase}/${landlordId}/invoices`,
			payload
		);
		return response.data;
	};

	const issueEfrisForPayment = async (landlordId, paymentId) => {
		if (!landlordId || !paymentId) {
			return { success: false, message: "Missing landlord or payment." };
		}
		setSubmitting(true);
		try {
			const data = await issueInvoice(landlordId, { payment_id: paymentId });
			return { success: true, data };
		} catch (error) {
			return {
				success: false,
				message: einvoicingErrorMessage(error, "Failed to generate EFRIS receipt."),
			};
		} finally {
			setSubmitting(false);
		}
	};

	return { isSubmitting, setEinvoicingEnabled, issueEfrisForPayment, issueInvoice };
};
